import React, { useMemo } from 'react';
import { Text, View } from '@react-pdf/renderer';
import { RHeader, RPage, RViewer } from '../../../components/PdfRender';
import { RSignature } from '../../../components/PdfRender/RSignature';
import { IDentalTreatment, IDentalTreatmentItem } from '../../interfaces';
import * as S from './styles';
import { useCompany } from '../../../hooks/company';
import { currency } from '../../utils/functions';
import { formatDate } from '../../utils/format-date';
import {
  carregarDentesIniciais,
  getFaceValueFromName,
} from '../../../components/odontogram/odontogram.helpers';
import {
  conditionConfig,
  PaintCondition,
  Tooth,
  ToothCondition,
} from '../../../components/odontogram/odontogram.types';
import { OdontogramPdf } from './OdontogramPdf';
import { useAuth } from '../../../hooks/auth';

interface IDentalTreatmentReportProps {
  treatment: IDentalTreatment | null;
  items: IDentalTreatmentItem[];
  validationCode?: string | null;
  signature?: string | null;
  signerName?: string | null;
}

export const DentalTreatmentReport: React.FC<IDentalTreatmentReportProps> = ({
  treatment,
  items,
  validationCode,
  signature,
  signerName,
}) => {
  const { company } = useCompany();
  const { clinic } = useAuth();
  const today = new Date().toLocaleDateString('fr-CA', {
    timeZone: 'America/Araguaina',
  });

  const date =
    treatment?.created_at != undefined
      ? treatment?.created_at?.toString().split('T')[0]
      : today;

  const odontogramState = useMemo(() => {
    const arcadas = JSON.parse(JSON.stringify(carregarDentesIniciais()));

    ['adultoCima', 'adultoBaixo', 'criancaCima', 'criancaBaixo'].forEach(
      (key) => {
        if ((arcadas as any)[key]) {
          (arcadas as any)[key].forEach((dente: Tooth) => {
            for (let i = 1; i <= 10; i++) dente.classes[`class${i}`] = '';
          });
        }
      },
    );

    if (!items || items.length === 0) return arcadas;

    items.forEach((item) => {
      if (!item.tooth_number || !item.current_state) return;

      let targetTooth: Tooth | undefined;
      let arcadaKey: string | undefined;
      let toothIndex = -1;

      for (const key in arcadas) {
        const idx = (arcadas as any)[key].findIndex(
          (d: Tooth) => String(d.numTooth) === String(item.tooth_number),
        );
        if (idx !== -1) {
          targetTooth = (arcadas as any)[key][idx];
          arcadaKey = key;
          toothIndex = idx;
          break;
        }
      }

      if (!targetTooth || !arcadaKey) return;

      const condicao = Number(item.current_state) as PaintCondition;
      const config = conditionConfig[condicao];

      if (!config) return;

      let faceValor = getFaceValueFromName(item.face);
      if (faceValor === 0 && item.face) {
        const map: Record<string, number> = {
          O: 5,
          M: 1,
          D: 2,
          V: 3,
          L: 4,
          P: 4,
        };
        const key = String(item.face).toUpperCase();
        faceValor = map[key] || 0;
      }

      const dente = (arcadas as any)[arcadaKey][toothIndex];
      dente.updated = 1;
      dente.currentState = condicao;

      switch (condicao) {
        case ToothCondition.AUSENTE:
          dente.classes['class6'] = config.classe;
          dente.classes['class7'] = config.classe;
          for (let i = 1; i <= 5; i++) dente.classes[`class${i}`] = 'hidden';
          break;

        case ToothCondition.IMPLANTE:
          dente.classes['class10'] = config.classe;
          for (let i = 1; i <= 5; i++) dente.classes[`class${i}`] = 'hidden';
          break;

        case ToothCondition.COROA:
          dente.classes['class9'] = config.classe;
          break;

        case ToothCondition.ENDODONTIA:
          dente.classes['class9'] = config.classe;
          break;

        case ToothCondition.OUTROS:
          dente.classes['class9'] = config.classe;
          break;

        case ToothCondition.AMALGAMA:
        case ToothCondition.CARIES:
        case ToothCondition.RESINA:
        case ToothCondition.SELANTE:
          if (faceValor > 0) {
            const classeKey = `class${faceValor}`;
            dente.classes[classeKey] = config.classe;
          }
          break;

        default:
          if (faceValor > 0) {
            dente.classes[`class${faceValor}`] = config.classe;
          }
          break;
      }
    });

    return arcadas;
  }, [items]);

  if (!treatment) return <></>;

  return (
    <RViewer>
      <RPage>
        <View style={S.Styles.container}>
          <RHeader company={company} />

          {/* Seção do Cabeçalho do Tratamento */}
          <View style={S.Styles.treatmentHeader}>
            <Text style={S.Styles.treatmentHeaderTitle}>
              Tratamento Odontológico
            </Text>
            <View style={S.Styles.dataGrid}>
              <View style={S.Styles.dataColumn}>
                <View style={S.Styles.row}>
                  <Text style={S.Styles.label}>Nº:</Text>
                  <Text style={S.Styles.value}>{treatment?.code}</Text>
                </View>
              </View>
              <View style={S.Styles.dataColumn}>
                <View style={S.Styles.row}>
                  <Text style={S.Styles.label}>Cadastrado:</Text>
                  <Text style={S.Styles.value}>{formatDate(`${date}`)}</Text>
                </View>
              </View>
            </View>
          </View>

          {/* Seção de Informações do Cliente */}
          <View style={S.Styles.clientSection}>
            <Text style={S.Styles.sectionTitle}>Dados do Paciente</Text>
            <View style={S.Styles.dataGrid}>
              <View style={S.Styles.dataColumn}>
                <View style={S.Styles.row}>
                  <Text style={S.Styles.label}>Nome:</Text>
                  <Text style={S.Styles.value}>{treatment?.client_name}</Text>
                </View>
                <View style={S.Styles.row}>
                  <Text style={S.Styles.label}>CPF:</Text>
                  <Text style={S.Styles.value}>
                    {treatment?.client_cpf_cnpj}
                  </Text>
                </View>
              </View>
              <View style={S.Styles.dataColumn}>
                <View style={S.Styles.row}>
                  <Text style={S.Styles.label}>Data Nasc.:</Text>
                  <Text style={S.Styles.value}>
                    {formatDate(treatment?.client_birthday)}
                  </Text>
                </View>
                <View style={S.Styles.row}>
                  <Text style={S.Styles.label}>Celular:</Text>
                  <Text style={S.Styles.value}>
                    {treatment?.client_cell_phone}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          {/* Seção do Odontograma */}
          <View style={S.Styles.odontogramSection}>
            <Text style={S.Styles.odontogramTitle}>Odontograma</Text>
            <View style={S.Styles.odontogramContainer}>
              <OdontogramPdf
                arcadaCima={odontogramState.adultoCima}
                arcadaBaixo={odontogramState.adultoBaixo}
                criancaCima={odontogramState.criancaCima}
                criancaBaixo={odontogramState.criancaBaixo}
              />
            </View>
          </View>

          {/* Seção de Procedimentos */}
          <View style={S.Styles.proceduresSection}>
            <Text style={S.Styles.proceduresTitle}>
              Procedimentos Realizados
            </Text>
            <View style={S.Styles.tableContainer}>
              <View style={S.Styles.rowHeader}>
                <Text style={S.Styles.columnHeader}>#</Text>
                <Text style={S.Styles.columnHeader}>Dente</Text>
                <Text style={S.Styles.columnHeader}>Face</Text>
                <Text style={S.Styles.columnHeader}>Estado</Text>
                <Text style={S.Styles.columnHeader}>Procedimento</Text>
                <Text style={S.Styles.columnHeader}>Valor Un.</Text>
                <Text style={S.Styles.columnHeader}>Desconto</Text>
                <Text style={S.Styles.columnHeader}>Total</Text>
              </View>
              {items?.map((item, index) => (
                <View key={item.id} style={S.Styles.rowBody}>
                  <Text style={S.Styles.columnBody}>{index + 1}</Text>
                  <Text style={S.Styles.columnBody}>{item.tooth_number}</Text>
                  <Text style={S.Styles.columnBody}>{item.face}</Text>
                  <Text style={S.Styles.columnBody}>
                    {
                      conditionConfig[item.current_state as PaintCondition]
                        ?.nome
                    }
                  </Text>
                  <Text style={S.Styles.columnBody}>{item.procedure_name}</Text>
                  <Text style={S.Styles.columnBody}>
                    {currency(item.value || 0)}
                  </Text>
                  <Text style={S.Styles.columnBody}>
                    {currency(item.discount || 0)}
                  </Text>
                  <Text style={S.Styles.columnBody}>
                    {currency((item.value || 0) - (item.discount || 0))}
                  </Text>
                </View>
              ))}
            </View>
          </View>

          {/* Seção de Totais */}
          <View style={S.Styles.totalsSection}>
            <Text style={S.Styles.totalsTitle}>Resumo Financeiro</Text>
            <View style={S.Styles.totalsGrid}>
              <View style={{ flex: 1 }}>
                <View style={S.Styles.totalRow}>
                  <Text style={S.Styles.totalLabel}>SubTotal:</Text>
                  <Text style={S.Styles.totalValue}>
                    {currency(treatment?.total)}
                  </Text>
                </View>
                <View style={S.Styles.totalRow}>
                  <Text style={S.Styles.totalLabel}>Desconto:</Text>
                  <Text style={S.Styles.totalValue}>
                    {currency(treatment.discount)}
                  </Text>
                </View>
                <View style={[S.Styles.totalRow, S.Styles.finalTotal]}>
                  <Text style={S.Styles.finalTotalLabel}>Total:</Text>
                  <Text style={S.Styles.finalTotalValue}>
                    {currency(treatment?.total - treatment?.discount)}
                  </Text>
                </View>
              </View>
            </View>
          </View>
          <RSignature
            professional_name={treatment?.professional_name}
            signer_name={signerName || treatment?.professional_name}
            signature={signature}
            signed_at={treatment?.signed_at}
            separate_page_when_signed
            validation_code={
              validationCode || treatment?.signature_validation_code
            }
            clinic={clinic}
          />
        </View>
      </RPage>
    </RViewer>
  );
};
